<script setup lang="ts">
    import { onMounted, reactive } from 'vue';
    import List from '@shared/components/List/List.vue';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import { SwitchStyle } from '@shared/components/Form/Form';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import { getAccountFeatures, disableFeature, enableFeature, type AccountFeatureItem } from './DisabledFeatures.access';

    const props = defineProps<{ accountId: string }>();

    const state = reactive<{
        loading: boolean;
        saving: string | null;
        features: AccountFeatureItem[];
    }>({
        loading: true,
        saving: null,
        features: [],
    });

    onMounted(async () => {
        state.features = await getAccountFeatures(props.accountId);
        state.loading = false;
    });

    async function toggle(feature: AccountFeatureItem): Promise<void> {
        if (state.saving) return;
        state.saving = feature.FeatureId;

        try {
            if (feature.IsDisabled) {
                await enableFeature(props.accountId, feature.FeatureId);
                feature.IsDisabled = false;
            } else {
                await disableFeature(props.accountId, feature.FeatureId);
                feature.IsDisabled = true;
            }
            bsAlert();
        } catch {
            bsAlert({ title: 'Error', content: 'Something went wrong', type: AlertTypes.Danger });
        } finally {
            state.saving = null;
        }
    }
</script>

<template>
    <List :items="state.features" itemId="FeatureId" displayAs="table" :tableHeaders="['Feature', 'Enabled']" :loading="state.loading" hideToolbar>
        <template #item="{ item: feature }">
            <td>{{ feature.Name }}</td>
            <td>
                <FormCheckable
                    label="Enable"
                    :switchStyle="SwitchStyle.RedToGreen"
                    :checked="!feature.IsDisabled"
                    :disabled="state.saving === feature.FeatureId"
                    @update:modelValue="() => toggle(feature)" />
            </td>
        </template>
    </List>
</template>
